import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import { AuthProvider } from './context/AuthContext';
import { CookieProvider } from './context/CookieContext';
import { CartProvider } from './context/CartContext';
import { CookieBanner } from './components/CookieBanner';
import { MatomoTracker } from './components/MatomoTracker';
import { AdminAuthProvider } from './context/AdminAuthContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { Navbar } from './components/Navbar';
import { Footer } from './components/Footer';
import { PortalLayout } from './components/PortalLayout';
import { AdminLayout } from './components/AdminLayout';
import { ScrollToTop } from './components/ScrollToTop';
import UnifiedCart from './components/UnifiedCart';
import { HomePage } from './pages/HomePage';
import { PreisePage } from './pages/PreisePage';
import { FeaturesPage } from './pages/FeaturesPage';
import { TechnikPage } from './pages/TechnikPage';
import { FAQPage } from './pages/FAQPage';
import { DomainsPage } from './pages/DomainsPage';
import { CheckoutPage } from './pages/CheckoutPage';
import CartCheckoutPage from './pages/CartCheckoutPage';
import { ProvisioningPage } from './pages/ProvisioningPage';
import { LoginPage } from './pages/LoginPage';
import { ImpressumPage } from './pages/ImpressumPage';
import { DatenschutzPage } from './pages/DatenschutzPage';
import SLAPage from './pages/SLAPage';
import AGBPage from './pages/AGBPage';
import AVVPage from './pages/AVVPage';
import { PortalDashboard } from './pages/portal/PortalDashboard';
import { PortalInstanz } from './pages/portal/PortalInstanz';
import { PortalWolken } from './pages/portal/PortalWolken';
import { PortalTarif } from './pages/portal/PortalTarif';
import { PortalRechnungen } from './pages/portal/PortalRechnungen';
import { PortalSupport } from './pages/portal/PortalSupport';
import { PortalSupportDetail } from './pages/portal/PortalSupportDetail';
import { PortalEinstellungen } from './pages/portal/PortalEinstellungen';
import { PortalDomains } from './pages/portal/PortalDomains';
import { PortalAVV } from './pages/portal/PortalAVV';
// Admin Pages
import { AdminLogin } from './pages/admin/AdminLogin';
import { AdminDashboard } from './pages/admin/AdminDashboard';
import { AdminOrders } from './pages/admin/AdminOrders';
import { AdminClouds } from './pages/admin/AdminClouds';
import { AdminCloudDetail } from './pages/admin/AdminCloudDetail';
import { AdminCustomers } from './pages/admin/AdminCustomers';
import { AdminCustomerDetail } from './pages/admin/AdminCustomerDetail';
import { AdminCoupons } from './pages/admin/AdminCoupons';
import { AdminSubscriptions } from './pages/admin/AdminSubscriptions';
import { AdminAdmins } from './pages/admin/AdminAdmins';
import { AdminProfile } from './pages/admin/AdminProfile';
import { AdminTickets } from './pages/admin/AdminTickets';
import { AdminTicketDetail } from './pages/admin/AdminTicketDetail';
import { AdminMailLog } from './pages/admin/AdminMailLog';
import AdminOrderDetail from './pages/admin/AdminOrderDetail';
import './App.css';

export default function App() {
  return (
    <Router>
      <CookieProvider>
        <AuthProvider>
          <CartProvider>
          <MatomoTracker />
          <ScrollToTop />
          <Toaster position="top-right" />
          <CookieBanner />
          <UnifiedCart />
        <Routes>
          {/* Public Routes with Navbar + Footer */}
          <Route
            path="/*"
            element={
              <div className="App min-h-screen bg-background flex flex-col">
                <Navbar />
                <main className="flex-1">
                  <Routes>
                    <Route path="/" element={<HomePage />} />
                    <Route path="/preise" element={<PreisePage />} />
                    <Route path="/features" element={<FeaturesPage />} />
                    <Route path="/technik" element={<TechnikPage />} />
                    <Route path="/faq" element={<FAQPage />} />
                    <Route path="/domains" element={<DomainsPage />} />
                    <Route path="/checkout" element={<CheckoutPage />} />
                    <Route path="/warenkorb" element={<CartCheckoutPage />} />
                    <Route path="/provisioning/:orderId" element={<ProvisioningPage />} />
                    <Route path="/login" element={<LoginPage />} />
                    <Route path="/impressum" element={<ImpressumPage />} />
                    <Route path="/datenschutz" element={<DatenschutzPage />} />
                    <Route path="/agb" element={<AGBPage />} />
                    <Route path="/avv" element={<AVVPage />} />
                    <Route path="/sla" element={<SLAPage />} />
                  </Routes>
                </main>
                <Footer />
              </div>
            }
          />

          {/* Portal Routes - Protected with Auth */}
          <Route
            path="/portal/*"
            element={
              <ProtectedRoute>
                <PortalLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<PortalDashboard />} />
            <Route path="wolken" element={<PortalWolken />} />
            <Route path="instanz/:instanceId" element={<PortalInstanz />} />
            <Route path="tarif" element={<PortalTarif />} />
            <Route path="rechnungen" element={<PortalRechnungen />} />
            <Route path="domains" element={<PortalDomains />} />
            <Route path="avv" element={<PortalAVV />} />
            <Route path="support" element={<PortalSupport />} />
            <Route path="support/:ticketId" element={<PortalSupportDetail />} />
            <Route path="einstellungen" element={<PortalEinstellungen />} />
          </Route>

          {/* Legacy /app route redirect to /portal */}
          <Route
            path="/app/*"
            element={
              <ProtectedRoute>
                <PortalLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<PortalDashboard />} />
            <Route path="wolken" element={<PortalWolken />} />
            <Route path="instanz/:instanceId" element={<PortalInstanz />} />
            <Route path="tarif" element={<PortalTarif />} />
            <Route path="rechnungen" element={<PortalRechnungen />} />
            <Route path="domains" element={<PortalDomains />} />
            <Route path="avv" element={<PortalAVV />} />
            <Route path="support" element={<PortalSupport />} />
            <Route path="support/:ticketId" element={<PortalSupportDetail />} />
            <Route path="einstellungen" element={<PortalEinstellungen />} />
          </Route>

          {/* Admin Routes - Separate Auth */}
          <Route path="/admin/login" element={
            <AdminAuthProvider>
              <AdminLogin />
            </AdminAuthProvider>
          } />
          <Route
            path="/admin/*"
            element={
              <AdminAuthProvider>
                <AdminLayout />
              </AdminAuthProvider>
            }
          >
            <Route index element={<AdminDashboard />} />
            <Route path="orders" element={<AdminOrders />} />
            <Route path="orders/:orderId" element={<AdminOrderDetail />} />
            <Route path="clouds" element={<AdminClouds />} />
            <Route path="clouds/:cloudId" element={<AdminCloudDetail />} />
            <Route path="customers" element={<AdminCustomers />} />
            <Route path="customers/:customerId" element={<AdminCustomerDetail />} />
            <Route path="tickets" element={<AdminTickets />} />
            <Route path="tickets/:ticketId" element={<AdminTicketDetail />} />
            <Route path="mail-log" element={<AdminMailLog />} />
            <Route path="coupons" element={<AdminCoupons />} />
            <Route path="subscriptions" element={<AdminSubscriptions />} />
            <Route path="admins" element={<AdminAdmins />} />
            <Route path="profile" element={<AdminProfile />} />
          </Route>
        </Routes>
        </CartProvider>
        </AuthProvider>
      </CookieProvider>
    </Router>
  );
}
